import { createContext, useContext, useState, useEffect } from 'react';
import api from '@/lib/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(() => localStorage.getItem('token'));
  const [permissoes, setPermissoes] = useState([]);
  const [cargoNome, setCargoNome] = useState(null);
  const [empresaAtiva, setEmpresaAtiva] = useState(null);
  const [empresasDisponiveis, setEmpresasDisponiveis] = useState([]);

  useEffect(() => {
    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      loadUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const loadUser = async () => {
    try {
      const [meRes, permsRes, empresasRes] = await Promise.all([
        api.get('/auth/me'),
        api.get('/auth/permissoes').catch(() => ({ data: { permissoes: [], cargo_nome: null } })),
        api.get('/empresas-oficina').catch(() => ({ data: [] })),
      ]);
      setUser(meRes.data);
      setPermissoes(permsRes.data.permissoes || []);
      setCargoNome(permsRes.data.cargo_nome || null);
      setEmpresaAtiva(meRes.data.empresa_ativa || null);
      setEmpresasDisponiveis(empresasRes.data || []);
    } catch (error) {
      console.error('Erro ao carregar usuário:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (username, password) => {
    try {
      const { data } = await api.post('/auth/login', { username, password });
      const newToken = data.access_token;
      localStorage.setItem('token', newToken);
      api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      setToken(newToken);
      await loadUser();
      return { success: true, needs_empresa_selection: data.needs_empresa_selection, empresas: data.empresas };
    } catch (error) {
      const message = error.response?.data?.detail || 'Erro ao fazer login';
      return { success: false, error: message };
    }
  };

  const trocarEmpresa = async (empresaId) => {
    try {
      const { data } = await api.post('/auth/select-empresa', { empresa_id: empresaId });
      const newToken = data.access_token;
      localStorage.setItem('token', newToken);
      api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      setToken(newToken);
      setEmpresaAtiva(data.empresa);
      await loadUser();
      return { success: true, empresa: data.empresa };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Erro ao trocar empresa' };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete api.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
    setPermissoes([]);
    setCargoNome(null);
    setEmpresaAtiva(null);
    setEmpresasDisponiveis([]);
  };

  const isAdmin = () => user?.role === 'admin';

  const hasPerm = (required) => {
    if (!required) return true;
    if (user?.role === 'admin') return true;
    if (permissoes.includes('*')) return true;
    return permissoes.includes(required);
  };

  return (
    <AuthContext.Provider value={{
      user, loading, token, login, logout, isAdmin, permissoes, cargoNome, hasPerm,
      empresaAtiva, empresasDisponiveis, trocarEmpresa,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
